import { api, unwrap } from './client';

/**
 * Expenses API client.
 *
 * Backend wraps every response in ApiResponse<T>; always pass through unwrap().
 * Paths begin with /finance/expenses.
 */

export async function listExpenses() {
  const res = await api.get('/finance/expenses');
  const d = unwrap(res);
  return Array.isArray(d) ? d : (d?.content || []);
}

export async function listMyExpenses() {
  const res = await api.get('/finance/expenses/my');
  const d = unwrap(res);
  return Array.isArray(d) ? d : (d?.content || []);
}

export async function listExpensesByDept(dept) {
  const res = await api.get(`/finance/expenses/dept/${dept}`);
  const d = unwrap(res);
  return Array.isArray(d) ? d : (d?.content || []);
}

export async function getExpense(id) {
  const res = await api.get(`/finance/expenses/${id}`);
  return unwrap(res);
}

/**
 * JSON variant — no file attached on creation.
 */
export async function createExpense(payload) {
  const res = await api.post('/finance/expenses', payload);
  return unwrap(res);
}

export async function updateExpense(id, payload) {
  const res = await api.put(`/finance/expenses/${id}`, payload);
  return unwrap(res);
}

/**
 * Multipart variant — backend expects:
 *   - `expense` part: a Blob with type application/json containing the
 *                     JSON-serialised ExpenseRequest.
 *   - `file`    part: the binary file.
 *
 * Using a typed Blob (rather than a plain string) ensures Spring binds the
 * `expense` request-part as JSON via its MappingJackson2HttpMessageConverter.
 */
export async function createExpenseWithFile(payload, file) {
  const fd = new FormData();
  fd.append(
    'expense',
    new Blob([JSON.stringify(payload)], { type: 'application/json' }),
  );
  if (file) fd.append('file', file);
  const res = await api.post('/finance/expenses', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

export async function submitExpense(id) {
  const res = await api.post(`/finance/expenses/${id}/submit`);
  return unwrap(res);
}

export async function deleteExpense(id) {
  const res = await api.delete(`/finance/expenses/${id}`);
  return unwrap(res);
}

// --- Paginated and Filtered Endpoints ---

export async function listExpensesPaginated(params = { page: 0, size: 10 }) {
  const res = await api.get('/finance/expenses/paginated/all', { params });
  return unwrap(res); // returns Page<ExpenseResponse>
}

export async function listMyExpensesPaginated(params = { page: 0, size: 10 }) {
  const res = await api.get('/finance/expenses/paginated/my', { params });
  return unwrap(res);
}

export async function filterExpenses(params) {
  const res = await api.get('/finance/expenses/paginated/filter', { params });
  return unwrap(res);
}

export async function filterExpensesByCategory(category, params = { page: 0, size: 10 }) {
  const res = await api.get(`/finance/expenses/paginated/category/${category}`, { params });
  return unwrap(res);
}

export async function filterExpensesByDept(dept, params = { page: 0, size: 10 }) {
  const res = await api.get(`/finance/expenses/paginated/dept/${dept}`, { params });
  return unwrap(res);
}

export async function filterExpensesByStatus(status, params = { page: 0, size: 10 }) {
  const res = await api.get(`/finance/expenses/paginated/status/${status}`, { params });
  return unwrap(res);
}

// --- Dashboard ---

export async function getExpenseDashboard(year) {
  const res = await api.get('/finance/expenses/dashboard', {
    params: year ? { year } : undefined,
  });
  return unwrap(res);
}

export async function bulkUploadExpenses(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await api.post('/finance/expenses/bulk-upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

